import { Navigate, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useFeatureFlagsReady } from "../contexts/launchDarklyContext";
import { useSoc1ExtractionWorkpaperEnabled } from "../hooks/useSoc1ExtractionWorkpaperEnabled";

/**
 * Guards the report-extraction routes on the soc1-extraction-workpaper flag.
 *
 * The flag MUST be read here, at the point of use, rather than threaded into
 * the router: AppRouter builds the router exactly once in a useState
 * initializer, and on the SSO/Optro callback path App() mounts fresh with no
 * access_token, so its useCheckTokenAndFetchUser instance early-returns and the
 * flag would be baked in as false. The callback page then stores tokens and
 * updates the shared auth context, but finishes with an in-SPA navigate, so
 * AppRouter never remounts and the frozen route table never catches up: the
 * sidebar item (which already reads the flag reactively) rendered while the
 * route behind it was the disabled fallback.
 *
 * Unlike the previous shouldEnableMidshipFeatures gate, this one is not a
 * synchronous function of the auth context, so it needs the same "flags not
 * ready" hold as RequireOptroBulkImport: useFeatureFlag returns its
 * fail-closed default until LaunchDarkly's initial fetch resolves, and then
 * the anonymous context's value until identify completes. Redirecting during
 * that window would bounce an entitled deep link straight back out.
 *
 * `redirectTo` is required rather than defaulted: the report-extraction paths
 * are mounted under both /dashboard and /audit, and each tree sends a blocked
 * visitor to its own landing page.
 */
export function RequireReportExtractions({
  redirectTo,
}: {
  redirectTo: string;
}) {
  const flagsReady = useFeatureFlagsReady();
  const enabled = useSoc1ExtractionWorkpaperEnabled();

  if (!flagsReady) {
    return (
      <div className="h-screen flex justify-center items-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!enabled) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}
